import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import * as Icons from 'react-icons/fa';
import { FILE_BASE_URL } from '../config/config';

const FaTrash = Icons.FaTrash as any;
const FaSave = Icons.FaSave as any;

interface SignaturePadProps {
  value?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  hasError?: boolean;
}

const Container = styled.div`
  width: 100%;
`;

const PreviewContainer = styled.div`
  margin-top: 1rem;
  border: 2px solid ${props => props.theme.border};
  border-radius: 8px;
  padding: 1rem;
  background: ${props => props.theme.surface};
`;

const PreviewLabel = styled.div`
  font-size: 0.875rem;
  color: ${props => props.theme.textSecondary};
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 200px;
  display: block;
  border-radius: 4px;
  border: 1px solid ${props => props.theme.border};
`;

const CanvasContainer = styled.div<{ hasError?: boolean }>`
  border: 2px dashed ${props => props.hasError ? props.theme.error : props.theme.border};
  border-radius: 8px;
  background: white;
  overflow: hidden;
  position: relative;
  touch-action: none;
  box-shadow: inset 0 1px 3px rgba(0, 0, 0, 0.1);
  min-height: 200px;
`;

const Canvas = styled.canvas`
  display: block;
  width: 100%;
  height: 200px;
  cursor: crosshair;
  position: relative;
  z-index: 1;
  background: white;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  flex-wrap: wrap;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 0.5rem 1rem;
  border-radius: 6px;
  border: none;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  transition: all 0.2s;
  
  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: ${props.theme.primary};
          color: white;
          &:hover:not(:disabled) {
            background: ${props.theme.primaryDark};
          }
        `;
      case 'danger':
        return `
          background: ${props.theme.error};
          color: white;
          &:hover:not(:disabled) {
            opacity: 0.9;
          }
        `;
      default:
        return `
          background: ${props.theme.surface};
          color: ${props.theme.textPrimary};
          border: 1px solid ${props.theme.border};
          &:hover:not(:disabled) {
            background: ${props.theme.border};
          }
        `;
    }
  }}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SavedIndicator = styled.span`
  display: flex;
  align-items: center;
  color: ${props => props.theme.success};
  font-weight: 500;
  font-size: 0.875rem;
  padding: 0.5rem 1rem;
`;

const SignaturePad: React.FC<SignaturePadProps> = ({ value, onChange, disabled = false, hasError = false }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const lastPointRef = useRef<{ x: number; y: number } | null>(null);

  // Initialize canvas size and context
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    // Set canvas size - use setTimeout to ensure container is fully rendered
    const initCanvas = () => {
      const container = canvas.parentElement;
      if (container) {
        const rect = container.getBoundingClientRect();
        const width = Math.floor(rect.width);
        
        // Set internal canvas dimensions to match display size
        canvas.width = width;
        canvas.height = 200;
        
        // Reinitialize context after size change with smooth settings
        const context = canvas.getContext('2d', { willReadFrequently: false });
        if (context) {
          context.strokeStyle = '#000';
          context.lineWidth = 3; // Increased for better visibility
          context.lineCap = 'round';
          context.lineJoin = 'round';
          context.imageSmoothingEnabled = true;
          context.imageSmoothingQuality = 'high';
          contextRef.current = context;
        }
      }
    };

    // Use setTimeout to ensure DOM is fully updated
    const timer = setTimeout(initCanvas, 50);
    return () => clearTimeout(timer);
  }, [value]); // Reinitialize when value changes (canvas visibility changes)

  // Load existing signature
  useEffect(() => {
    if (value) {
      if (value.startsWith('data:image/')) {
        // Base64 image
        loadSignature(value);
      } else if (value.startsWith('/') || value.startsWith('Data/')) {
        // File path from server - convert to full URL
        const imageUrl = value.startsWith('/') ? `${FILE_BASE_URL}${value}` : `${FILE_BASE_URL}/${value}`;
        loadSignature(imageUrl);
      } else if (value.startsWith('http')) {
        // Already a full URL
        loadSignature(value);
      }
    }
  }, [value]);

  useEffect(() => {
    // Update hasSignature when value changes
    setHasSignature(!!value);
  }, [value]);

  const loadSignature = (dataUrl: string) => {
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;

    const image = new Image();
    image.onload = () => {
      context.clearRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      setHasSignature(true);
    };
    image.src = dataUrl;
  };

  const startDrawing = (event: React.MouseEvent | React.TouchEvent) => {
    if (disabled) return;

    event.preventDefault();
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context) return;

    setIsDrawing(true);

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const x = (getEventX(event) - rect.left) * scaleX;
    const y = (getEventY(event) - rect.top) * scaleY;

    lastPointRef.current = { x, y };
    context.beginPath();
    context.moveTo(x, y);
  };

  const draw = (event: React.MouseEvent | React.TouchEvent) => {
    if (!isDrawing || disabled) return;

    event.preventDefault();
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (!canvas || !context || !lastPointRef.current) return;

    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    const currentX = (getEventX(event) - rect.left) * scaleX;
    const currentY = (getEventY(event) - rect.top) * scaleY;

    // Use quadratic curve for smoother lines
    const lastPoint = lastPointRef.current;
    const midX = (lastPoint.x + currentX) / 2;
    const midY = (lastPoint.y + currentY) / 2;

    context.quadraticCurveTo(lastPoint.x, lastPoint.y, midX, midY);
    context.stroke();
    context.beginPath();
    context.moveTo(midX, midY);

    lastPointRef.current = { x: currentX, y: currentY };
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    const context = contextRef.current;
    if (context && lastPointRef.current) {
      // Draw final point
      context.lineTo(lastPointRef.current.x, lastPointRef.current.y);
      context.stroke();
    }
    
    setIsDrawing(false);
    setHasSignature(true);
    lastPointRef.current = null;
    // Don't auto-save - user must click Save button
  };

  const getEventX = (event: React.MouseEvent | React.TouchEvent): number => {
    if ('touches' in event) {
      return event.touches[0].clientX;
    }
    return event.clientX;
  };

  const getEventY = (event: React.MouseEvent | React.TouchEvent): number => {
    if ('touches' in event) {
      return event.touches[0].clientY;
    }
    return event.clientY;
  };

  const clearSignature = () => {
    // Clear the canvas if it's available (when showing canvas view)
    const canvas = canvasRef.current;
    const context = contextRef.current;
    if (canvas && context) {
      context.clearRect(0, 0, canvas.width, canvas.height);
    }
    
    // Always clear the value and state to show empty canvas
    lastPointRef.current = null;
    setHasSignature(false);
    onChange('');
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    try {
      const dataURL = canvas.toDataURL('image/png');
      onChange(dataURL);
      setHasSignature(true);
    } catch (error) {
      console.error('Error saving signature:', error);
    }
  };

  // Get the image URL for display
  const getImageUrl = () => {
    if (!value) return null;
    
    if (value.startsWith('data:image/')) {
      return value; // Base64
    } else if (value.startsWith('http')) {
      return value; // Full URL
    } else if (value.startsWith('/') || value.startsWith('Data/')) {
      // Server file path
      return value.startsWith('/') ? `${FILE_BASE_URL}${value}` : `${FILE_BASE_URL}/${value}`;
    }
    
    return null;
  };

  const imageUrl = getImageUrl();

  // Clear invalid text values whenever value changes
  useEffect(() => {
    if (value && !imageUrl) {
      // Value exists but is not a valid image (e.g., plain text)
      console.log('Clearing invalid signature value:', value);
      onChange('');
    }
  }, [value, imageUrl]);

  return (
    <Container>
      {/* Show canvas only when no signature or when user wants to redraw */}
      {!value && (
        <CanvasContainer hasError={hasError}>
          <Canvas
            ref={canvasRef}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawing}
            onTouchMove={draw}
            onTouchEnd={stopDrawing}
          />
        </CanvasContainer>
      )}

      {/* Show image preview when signature is saved */}
      {value && imageUrl && (
        <PreviewContainer>
          <PreviewLabel>Saved Signature:</PreviewLabel>
          <PreviewImage src={imageUrl} alt="Signature" onError={(e) => {
            console.error('Failed to load signature image:', imageUrl);
            // If image fails to load, show canvas instead
            setHasSignature(false);
            onChange('');
          }} />
        </PreviewContainer>
      )}

      <ButtonGroup>
        {/* Show Save and Clear buttons when canvas has drawing but not saved */}
        {!value && hasSignature && (
          <>
            <Button
              type="button"
              variant="primary"
              onClick={saveSignature}
              disabled={disabled}
            >
              <FaSave />
              Save Signature
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={clearSignature}
              disabled={disabled}
            >
              <FaTrash />
              Clear
            </Button>
          </>
        )}
        
        {/* Show Clear & Redraw button when signature is saved */}
        {value && (
          <>
            <Button
              type="button"
              variant="danger"
              onClick={clearSignature}
              disabled={disabled}
            >
              <FaTrash />
              Clear & Redraw
            </Button>
            <SavedIndicator>
              ✓ Signature saved
            </SavedIndicator>
          </>
        )}
      </ButtonGroup>
    </Container>
  );
};

export default SignaturePad;

